import { gridLayout } from './layout';
import type { ClientHost } from './mode';
import { C, T } from './paint';
import { renderDrawing, replayDuration, setStrokeCanvasFactory, STROKE_BRUSH_NAMES } from '../shared/art/drawing';
import { PALETTES } from '../shared/account';
import { DRAWING_EXAMPLES, LESSON_LEVELS, type LessonLevel, type DrawingLesson } from './drawing-examples-data';
import { DrawingLessonReplay } from './drawing-lesson-replay';

/** 教学内容完全内置，独立于玩家作品与模式状态。 */
export class DrawingExamplesClient {
  page = 0;
  private pageCapacity = 4;
  index = 0;
  level: LessonLevel = 0;
  query = '';
  searchKeyboardOpen = false;
  searchKeyboardHeight = 0;
  private searchKeyboardPixels = 0;
  position = 0;
  speed = 1;
  playing = false;
  private tick = 0;
  private rendered = '';
  private selected?: DrawingLesson;
  private buffer: HTMLCanvasElement;
  private replay: DrawingLessonReplay;
  private thumbnails = new Map<string, HTMLCanvasElement>();
  get count() { return DRAWING_EXAMPLES.length; }
  get searchItems() {
    const query = this.query.trim().toLocaleLowerCase('zh-CN');
    return query ? DRAWING_EXAMPLES.filter(item => [item.title, item.skill, ...item.keywords].some(value => value.toLocaleLowerCase('zh-CN').includes(query))) : DRAWING_EXAMPLES;
  }
  constructor(private host: ClientHost) {
    this.buffer = host.platform.createCanvas(600, 600);
    this.replay = new DrawingLessonReplay((w, h) => host.platform.createCanvas(w, h), 600);
  }
  get lesson() {
    const item = DRAWING_EXAMPLES[this.index];
    if (!this.selected || this.selected.id !== item.id || this.selected.level !== this.level) this.selected = item.create(this.level);
    return this.selected;
  }
  get total() { return replayDuration(this.lesson.drawing); }
  get stepIndex() { let index = 0; this.lesson.steps.forEach((step, i) => { if (step.at <= this.position) index = i; }); return index; }
  openList() { this.pause(); this.host.go('drawing-examples'); }
  open(index: number) {
    this.index = Math.max(0, Math.min(DRAWING_EXAMPLES.length - 1, index));
    this.position = 0; this.speed = 1; this.playing = true; this.tick = Date.now(); this.rendered = '';
    this.host.go('drawing-example');
  }
  update(now: number) {
    if (this.playing) {
      this.position = Math.min(this.total, this.position + Math.max(0, now - this.tick) * this.speed);
      if (this.position >= this.total) this.playing = false;
    }
    this.tick = now;
  }
  pause() { this.update(Date.now()); this.playing = false; }
  seek(position: number) { this.position = Math.max(0, Math.min(this.total, position)); this.tick = Date.now(); if (this.position >= this.total) this.playing = false; }
  toggle() { this.update(Date.now()); if (this.position >= this.total) this.seek(0); this.playing = !this.playing; }
  changeSpeed() { this.update(Date.now()); this.speed = this.speed === 1 ? 2 : this.speed === 2 ? 4 : 1; }
  selectLevel(level: LessonLevel) {
    if (level === this.level) return;
    this.pause(); this.level = level; this.selected = undefined;
    if (this.host.screen === 'drawing-example') this.open(this.index);
  }
  private levelButtons(y: number) {
    LESSON_LEVELS.forEach((level, i) => this.host.paint.button(`example-level-${i}`, `${i + 1} · ${level.label}`, this.host.x(24 + i * 116), y, this.host.paint.span(110), this.host.tablet ? 44 : 32, () => this.selectLevel(i as LessonLevel), this.level === i ? 'secondary' : 'plain'));
  }
  private materials() {
    this.pause(); const lesson = this.lesson;
    const brushes = lesson.brushes.map(id => STROKE_BRUSH_NAMES[id]).join('、');
    const palettes = lesson.palettes.map(id => PALETTES.find(p => p.id === id)!.name).join('、');
    void this.host.run(async () => { await this.host.platform.confirm('本课用色与画笔', `画笔：${brushes}。\n色包：${palettes}。\n${LESSON_LEVELS[this.level].description}。所有教程均可直接观看；临摹时可用相近的已有颜色代替。`); });
  }
  private async search() {
    if (this.searchKeyboardOpen) return;
    if (!this.host.platform.openKeyboard) {
      const value = await this.host.platform.prompt('搜索画画例子', '输入题目、分类或关键词', this.query);
      if (value !== null) { this.query = value.trim(); this.page = 0; }
      return;
    }
    this.searchKeyboardOpen = true;
    try {
      await this.host.platform.openKeyboard({
        value: this.query, maxLength: 24,
        onInput: value => { this.query = value; this.page = 0; },
        onConfirm: value => { this.query = value.trim(); this.page = 0; },
        onClose: () => { this.searchKeyboardOpen = false; this.searchKeyboardHeight = 0; },
        onHeight: height => { this.searchKeyboardPixels=height; this.searchKeyboardHeight = height / this.host.layout.scale; },
      });
    } catch { this.searchKeyboardOpen = false; this.searchKeyboardHeight = 0; }
  }
  private thumbnail(index: number) {
    const item = DRAWING_EXAMPLES[index], key = `${item.id}:${this.level}`; let canvas = this.thumbnails.get(key);
    if (!canvas) {
      const lesson = item.create(this.level); canvas = this.host.platform.createCanvas(300, 300);
      renderDrawing(canvas.getContext('2d')!, lesson.drawing, 300);
      if (this.thumbnails.size >= 8) this.thumbnails.delete(this.thumbnails.keys().next().value!);
      this.thumbnails.set(key, canvas);
    }
    return canvas;
  }
  resize() { if(this.searchKeyboardOpen)this.searchKeyboardHeight=this.searchKeyboardPixels/this.host.layout.scale; }
  listScreen() {
    const p = this.host.paint, h = this.host.H - this.host.top - this.searchKeyboardHeight;
    setStrokeCanvasFactory((w, h) => this.host.platform.createCanvas(w, h));
    this.host.header('画画例子', `${this.count} 个题材 × 3 级教程 · 从第一笔到完整创作`);
    const searchY = 88;
    p.box(p.x(24), searchY, p.span(342), 36, C.white, 18, C.line, 8);
    p.ctx.strokeStyle = C.mute; p.ctx.lineWidth = 2;
    p.ctx.beginPath(); p.ctx.arc(43, searchY + 16, 6, 0, Math.PI * 2); p.ctx.stroke();
    p.ctx.beginPath(); p.ctx.moveTo(47, searchY + 21); p.ctx.lineTo(52, searchY + 26); p.ctx.stroke();
    p.text(this.query || '搜索例子、题材或关键词', p.x(64), searchY + 18, T.caption, this.query ? C.ink : C.mute);
    if (this.query) p.text('×', p.x(344), searchY + 18, 19, C.mute, '600', 'center');
    p.hits.push({ id: 'examples-search', label: '搜索画画例子', x: p.x(24), y: searchY, w: p.span(this.query ? 294 : 342), h: 36, action: () => { void this.search(); } });
    if (this.query) p.hits.push({ id: 'examples-search-clear', label: '清除搜索', x: p.x(317), y: searchY, w: 49, h: 36, action: () => { this.query = ''; this.page = 0; } });
    this.levelButtons(134);
    const items = this.searchItems;
    const grid = gridLayout(this.host.W, this.host.tablet, 181, h - 58, 62);
    const {columns, cardWidth, cardHeight:cardH, gap, capacity} = grid;
    if(capacity !== this.pageCapacity) { this.page=Math.floor(this.page*this.pageCapacity/capacity);this.pageCapacity=capacity; }
    const pages = Math.max(1, Math.ceil(items.length / capacity)); this.page = Math.min(this.page, pages - 1);
    const visible = items.slice(this.page * capacity, (this.page + 1) * capacity);
    visible.forEach((lesson, i) => {
      const originalIndex = DRAWING_EXAMPLES.findIndex(item => item.id === lesson.id);
      const x = 24 + i % columns * (cardWidth + gap), y = 181 + Math.floor(i / columns) * (cardH + gap), size = Math.min(cardWidth - 24, cardH - 62);
      p.card(x, y, cardWidth, cardH, 9);
      p.ctx.drawImage(this.thumbnail(originalIndex), x + (cardWidth - size) / 2, y + 8, size, size);
      p.text(lesson.title, x + 12, y + cardH - 37, T.bodyStrong, C.ink, '700');
      p.fit(lesson.skill, x + 12, y + cardH - 16, cardWidth - 24, T.note, C.mute);
      p.hits.push({ id: `example-${lesson.id}`, label: `观看${lesson.title}${LESSON_LEVELS[this.level].label}教程`, x, y, w: cardWidth, h: cardH, action: () => this.open(originalIndex) });
    });
    const y = h - 48;
    p.button('examples-prev', '‹ 上一页', p.x(24), y, p.span(104), 34, () => this.page--, 'plain', this.page === 0);
    p.text(items.length ? `${this.page + 1} / ${pages}` : '没有匹配的例子', p.x(195), y + 17, T.caption, C.mute, '400', 'center');
    p.button('examples-next', '下一页 ›', p.x(262), y, p.span(104), 34, () => this.page++, 'plain', this.page + 1 >= pages);
  }
  playerScreen(now: number) {
    setStrokeCanvasFactory((w, h) => this.host.platform.createCanvas(w, h));
    this.update(now);
    const p = this.host.paint, h = this.host.H - this.host.top, lesson = this.lesson;
    this.host.header(lesson.title, lesson.style);
    this.levelButtons(102);
    const colors = new Set(lesson.drawing.strokes.map(s => s.color));
    if (lesson.drawing.background) colors.add(lesson.drawing.background);
    const materialsY=this.host.tablet?174:150;
    p.text(`${colors.size} 色 · ${lesson.brushes.length} 种笔头`, p.x(28), materialsY, T.note, C.mute);
    p.text('用色与画笔 ›', p.x(362), materialsY, T.caption, C.accentDeep, '600', 'right');
    p.hits.push({ id: 'example-materials', label: '查看本课使用的色包与画笔', x: p.x(220), y: this.host.tablet?materialsY-22:138, w: p.span(146), h: this.host.tablet?44:25, action: () => this.materials() });
    const size = Math.min(this.host.W - 48, Math.max(100, h - (this.host.tablet?469:423))), x = (this.host.W - size) / 2, y = this.host.tablet?205:171;
    const key = `${lesson.id}:${lesson.level}:${this.position}`;
    if (this.rendered !== key) { this.replay.render(this.buffer.getContext('2d')!, lesson.drawing, this.position); this.rendered = key; }
    p.frame(x - 4, y - 4, size + 8, size + 8); p.ctx.drawImage(this.buffer, x, y, size, size);
    let row = y + size + 17;
    const seconds = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}`;
    p.fit(this.position >= this.total ? '完成啦！可以换一级试试看。' : `第 ${this.stepIndex + 1} / ${lesson.steps.length} 步 · ${lesson.steps[this.stepIndex].title}`, p.x(24), row, p.span(245), T.caption, C.accentDeep, '600');
    p.text(`${seconds(this.position)} / ${seconds(this.total)}`, p.x(366), row, T.note, C.mute, '400', 'right');
    row += 17;
    p.box(p.x(24), row, p.span(342), 5, C.line, 3); if (this.position) p.box(p.x(24), row, Math.max(5, p.span(342) * this.position / this.total), 5, C.accent, 3);
    row += 16;
    p.button('example-play', this.playing ? '暂停' : this.position >= this.total ? '再看一遍' : '继续播放', p.x(24), row, p.span(111), 35, () => this.toggle(), 'primary');
    p.button('example-speed', `${this.speed} 倍速`, p.x(143), row, p.span(83), 35, () => this.changeSpeed(), 'plain');
    p.button('example-finish', '看成品', p.x(234), row, p.span(132), 35, () => { this.pause(); this.seek(this.total); }, 'secondary');
    row += this.host.tablet ? 58 : 46;
    const stepW = (this.host.W - 48 - (lesson.steps.length - 1) * 6) / lesson.steps.length;
    lesson.steps.forEach((step, i) => p.button(`example-step-${i}`, `${i + 1} 步`, 24 + i * (stepW + 6), row, stepW, 30, () => { this.pause(); this.seek(step.at); }, i === this.stepIndex ? 'secondary' : 'plain'));
    row += 43;
    p.wrap(lesson.steps[this.stepIndex].tip, p.x(28), row, p.span(334), T.caption, C.mute, 19);
    row += 48;
    p.button('example-restart', '从头播放', p.x(24), row, p.span(105), 35, () => { this.seek(0); this.playing = true; }, 'plain');
    p.button('example-list', '回到例画册', p.x(140), row, p.span(110), 35, () => this.openList(), 'plain');
    p.button('example-next', '下一幅 ›', p.x(261), row, p.span(105), 35, () => this.open((this.index + 1) % DRAWING_EXAMPLES.length), 'secondary');
  }
}
